import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { TooltipProps } from './Tooltip.types'
import { TooltipWrapper, TooltipContentPortal } from './Tooltip.styles'

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  delay = 200,
  maxWidth = 250,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<{
    top: number
    left: number
    placement: 'top' | 'bottom' | 'left' | 'right'
  } | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
    setPosition(null)
  }

  const updatePosition = useCallback(() => {
    if (!wrapperRef.current || !contentRef.current) return

    const rect = wrapperRef.current.getBoundingClientRect()
    const tooltipRect = contentRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const gap = 10

    let actualPlacement = placement

    // Check if tooltip goes off screen and adjust
    if (placement === 'top' && rect.top - tooltipRect.height - gap < 0) {
      actualPlacement = 'bottom'
    } else if (
      placement === 'bottom' &&
      rect.bottom + tooltipRect.height + gap > viewportHeight
    ) {
      actualPlacement = 'top'
    } else if (
      placement === 'right' &&
      rect.right + tooltipRect.width + gap > viewportWidth
    ) {
      actualPlacement = 'left'
    } else if (
      placement === 'left' &&
      rect.left - tooltipRect.width - gap < 0
    ) {
      actualPlacement = 'right'
    }

    let top = 0
    let left = 0

    switch (actualPlacement) {
      case 'top':
        top = rect.top - tooltipRect.height - gap
        left = rect.left + rect.width / 2 - tooltipRect.width / 2
        break
      case 'bottom':
        top = rect.bottom + gap
        left = rect.left + rect.width / 2 - tooltipRect.width / 2
        break
      case 'left':
        top = rect.top + rect.height / 2 - tooltipRect.height / 2
        left = rect.left - tooltipRect.width - gap
        break
      case 'right':
        top = rect.top + rect.height / 2 - tooltipRect.height / 2
        left = rect.right + gap
        break
    }

    // Clamp to viewport
    left = Math.max(8, Math.min(left, viewportWidth - tooltipRect.width - 8))
    top = Math.max(8, Math.min(top, viewportHeight - tooltipRect.height - 8))

    setPosition({ top, left, placement: actualPlacement })
  }, [placement])

  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure the portal content is rendered
      requestAnimationFrame(updatePosition)
    }
  }, [isVisible, updatePosition])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <TooltipWrapper
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible &&
        content &&
        createPortal(
          <TooltipContentPortal
            ref={contentRef}
            $placement={position?.placement ?? placement}
            $maxWidth={maxWidth}
            $top={position?.top ?? 0}
            $left={position?.left ?? 0}
            $positioned={position !== null}
          >
            {content}
          </TooltipContentPortal>,
          document.body
        )}
    </TooltipWrapper>
  )
}
